import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, X, Check, Loader2, ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import PasswordInput from './PasswordInput';

/**
 * "Cambia email" entry point + 2-step modal flow.
 * Step 1: current password + new email → backend sends OTP to new email.
 * Step 2: user types the 6-digit code → backend applies the change.
 */
export default function ChangeEmailButton() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { user, setUser } = useAuth();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('request'); // 'request' | 'confirm'
  const [currentPw, setCurrentPw] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setStep('request');
    setCurrentPw('');
    setNewEmail('');
    setCode('');
  };
  const close = () => { if (!saving) { setOpen(false); reset(); } };

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!newEmail.includes('@')) {
      toast.error(t('accountSecurity.invalidEmail', 'Email non valida'));
      return;
    }
    setSaving(true);
    try {
      const res = await httpClient.post(`${API_URL}/api/users/me/change-email/request`, {
        current_password: currentPw,
        new_email: newEmail.trim().toLowerCase(),
      });
      toast.success(res.data?.message || t('accountSecurity.codeSent', 'Codice inviato'));
      setStep('confirm');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('accountSecurity.genericError', 'Errore'));
    } finally {
      setSaving(false);
    }
  };

  const confirmOtp = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) {
      toast.error(t('accountSecurity.codeMustBe6', 'Inserisci il codice di 6 cifre'));
      return;
    }
    setSaving(true);
    try {
      const res = await httpClient.post(`${API_URL}/api/users/me/change-email/confirm`, { code });
      const revoked = res.data?.other_sessions_revoked || 0;
      const baseMsg = res.data?.message || t('accountSecurity.emailUpdated');
      if (revoked > 0) {
        toast.success(t('accountSecurity.updatedAndRevoked', { msg: baseMsg, n: revoked }));
      } else {
        toast.success(baseMsg);
      }
      // Update local auth context so UI reflects the new email immediately
      if (res.data?.email) {
        setUser((prev) => (prev ? { ...prev, email: res.data.email } : prev));
      }
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('accountSecurity.genericError', 'Errore'));
    } finally {
      setSaving(false);
    }
  };

  const inputBase = `w-full px-4 h-12 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 ${
    isDark
      ? 'bg-white/5 text-white border border-white/15 placeholder-white/50'
      : 'bg-gray-50 text-gray-900 border border-gray-200 placeholder-gray-400'
  }`;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`w-full glass-card-sm p-4 flex items-center gap-3 transition-colors ${
          isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-50'
        }`}
        data-testid="change-email-btn"
      >
        <Mail className="w-5 h-5 opacity-70" />
        <div className="flex-1 text-left min-w-0">
          <div className="font-medium">{t('accountSecurity.changeEmail', 'Cambia email')}</div>
          <div className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {user?.email || t('accountSecurity.changeEmailDesc', 'Aggiorna l\'indirizzo di accesso')}
          </div>
        </div>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
          onClick={close}
          data-testid="change-email-overlay"
        >
          <form
            onSubmit={step === 'request' ? requestOtp : confirmOtp}
            className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${
              isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-5 py-4 flex items-center justify-between border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <div className="flex items-center gap-2">
                {step === 'confirm' && (
                  <button
                    type="button"
                    onClick={() => !saving && setStep('request')}
                    className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                    data-testid="change-email-back"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {step === 'request'
                    ? t('accountSecurity.changeEmail', 'Cambia email')
                    : t('accountSecurity.enterCode', 'Inserisci il codice')}
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                data-testid="change-email-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {step === 'request' ? (
                <>
                  <PasswordInput
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    placeholder={t('accountSecurity.currentPassword', 'Password attuale')}
                    autoComplete="current-password"
                    autoFocus
                    className={inputBase}
                    iconColor={isDark ? 'text-white/60 hover:text-white/90' : 'text-gray-400 hover:text-gray-600'}
                    data-testid="change-email-current-pw"
                  />
                  <input
                    type="email"
                    inputMode="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder={t('accountSecurity.newEmail', 'Nuova email')}
                    className={inputBase}
                    data-testid="change-email-new-email"
                  />
                  <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {t('accountSecurity.newEmailHint', 'Ti invieremo un codice di 6 cifre alla nuova email per confermare.')}
                  </p>
                </>
              ) : (
                <>
                  <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {t('accountSecurity.codeSentTo', 'Abbiamo inviato un codice a')}{' '}
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{newEmail}</span>
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    autoFocus
                    className={`${inputBase} text-center font-mono text-lg tracking-[0.5em]`}
                    data-testid="change-email-code"
                  />
                </>
              )}
            </div>

            <div className={`p-4 flex items-center gap-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <button
                type="button"
                onClick={close}
                disabled={saving}
                className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'}`}
                data-testid="change-email-cancel"
              >
                {t('common.cancel', 'Annulla')}
              </button>
              <button
                type="submit"
                disabled={
                  saving ||
                  (step === 'request' && (!currentPw || !newEmail)) ||
                  (step === 'confirm' && code.length !== 6)
                }
                className="flex-1 py-3 rounded-xl font-semibold bg-[#E8334A] hover:bg-[#d42a40] text-white flex items-center justify-center gap-2 shadow-lg shadow-[#E8334A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="change-email-submit"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : step === 'request' ? (
                  <>{t('accountSecurity.sendCode', 'Invia codice')}</>
                ) : (
                  <><Check className="w-4 h-4" /> {t('common.confirm', 'Conferma')}</>
                )}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </>
  );
}
